import { useEffect, useMemo, useState, type ReactNode, type CSSProperties } from 'react';

type FilterOperator = 'contains' | 'equals' | 'not_contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty';

interface ColumnFilterState {
  operator: FilterOperator;
  text: string;
  selectedValues: string[] | null;
}

export interface FilterableColumnDef<T> {
  key: string;
  header: ReactNode;
  headerText?: string;
  value?: (row: T) => unknown;
  render?: (row: T, rowIndex: number) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
  headerStyle?: CSSProperties;
  cellStyle?: CSSProperties;
  sortable?: boolean;
  sortLabel?: ReactNode;
  onSort?: () => void;
  menuEnabled?: boolean;
  filterEnabled?: boolean;
  resizeEnabled?: boolean;
}

interface FilterableColumnTableProps<T> {
  columns: FilterableColumnDef<T>[];
  rows: T[];
  storageKey: string;
  rowKey?: (row: T, rowIndex: number) => string | number;
  rowClassName?: (row: T, rowIndex: number) => string;
  onRowClick?: (row: T, rowIndex: number) => void;
  emptyMessage?: string;
  maxHeightClassName?: string;
  tableClassName?: string;
  wrapperClassName?: string;
  canFilterColumns?: boolean;
  canResizeColumns?: boolean;
  onFilteredRowsChange?: (rows: T[]) => void;
  paginationEnabled?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

const FILTER_OPERATORS: Array<{ value: FilterOperator; label: string }> = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];

function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((item) => stringifyCell(item)).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function defaultFilter(): ColumnFilterState {
  return { operator: 'contains', text: '', selectedValues: null };
}

function isLongTextColumn(column: string): boolean {
  return /(raw|json|properties|property|entities|entity|description|details|message|comment|comments|command|commandline|url|query|data|evidence|additional|extended|title|name)/i.test(column);
}

function columnMaxWidth(column: string): number {
  return isLongTextColumn(column) ? 720 : 420;
}

function longestLineLength(value: string): number {
  return value.split(/\r?\n/).reduce((max, line) => Math.max(max, line.length), 0);
}

function estimateTextWidth(value: string): number {
  const chars = Math.min(longestLineLength(value), 120);
  return Math.ceil(chars * 7.4) + 42;
}

function matchesTextFilter(rawValue: string, filter: ColumnFilterState): boolean {
  const value = rawValue.trim();
  const term = filter.text.trim().toLowerCase();
  const lower = value.toLowerCase();

  if (filter.operator === 'is_empty') return value.length === 0;
  if (filter.operator === 'is_not_empty') return value.length > 0;
  if (!term) return true;

  switch (filter.operator) {
    case 'equals':
      return lower === term;
    case 'not_contains':
      return !lower.includes(term);
    case 'starts_with':
      return lower.startsWith(term);
    case 'ends_with':
      return lower.endsWith(term);
    case 'contains':
    default:
      return lower.includes(term);
  }
}

function isFilterActive(filter: ColumnFilterState | undefined, allValues?: string[]): boolean {
  if (!filter) return false;
  if (filter.operator === 'is_empty' || filter.operator === 'is_not_empty') return true;
  if (filter.text.trim()) return true;
  if (filter.selectedValues !== null) {
    const allCount = allValues?.length ?? 0;
    return filter.selectedValues.length !== allCount;
  }
  return false;
}

function loadWidths(storageKey: string): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const width = Number(value);
      if (Number.isFinite(width) && width >= 60 && width <= 1200) out[key] = width;
    }
    return out;
  } catch {
    return {};
  }
}

function saveWidths(storageKey: string, widths: Record<string, number>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // ignore localStorage errors in hardened browsers
  }
}

export function FilterableColumnTable<T>({
  columns,
  rows,
  storageKey,
  rowKey,
  rowClassName,
  onRowClick,
  emptyMessage = 'No rows match the current column filters.',
  maxHeightClassName = 'max-h-[620px]',
  tableClassName = 'w-full text-xs',
  wrapperClassName = '',
  canFilterColumns = true,
  canResizeColumns = true,
  onFilteredRowsChange,
  paginationEnabled = false,
  defaultPageSize = 50,
  pageSizeOptions = [25, 50, 100],
}: FilterableColumnTableProps<T>) {
  const [openColumnMenu, setOpenColumnMenu] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>({});
  const [valueSearch, setValueSearch] = useState<Record<string, string>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => loadWidths(storageKey));
  const normalizedPageSizes = useMemo(() => Array.from(new Set(pageSizeOptions.filter((value) => Number.isFinite(value) && value > 0))).sort((a, b) => a - b), [pageSizeOptions]);
  const [pageSize, setPageSize] = useState(() => Math.max(1, Math.round(defaultPageSize)));
  const [page, setPage] = useState(1);

  useEffect(() => {
    setColumnWidths(loadWidths(storageKey));
    setOpenColumnMenu(null);
  }, [storageKey]);

  useEffect(() => {
    saveWidths(storageKey, columnWidths);
  }, [storageKey, columnWidths]);

  const columnValues = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const column of columns) {
      const values = new Set<string>();
      for (const row of rows.slice(0, 5000)) {
        values.add(getColumnText(column, row).trim());
        if (values.size >= 500) break;
      }
      out[column.key] = Array.from(values).sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [columns, rows]);

  const filteredRows = useMemo(() => {
    const activeFilters = (Object.entries(filters) as Array<[string, ColumnFilterState]>).filter(([columnKey, filter]) =>
      isFilterActive(filter, columnValues[columnKey]),
    );
    if (activeFilters.length === 0) return rows;

    return rows.filter((row) => {
      for (const [columnKey, filter] of activeFilters) {
        const column = columns.find((c) => c.key === columnKey);
        if (!column) continue;
        const rawValue = getColumnText(column, row).trim();
        if (filter.selectedValues !== null && !filter.selectedValues.includes(rawValue)) return false;
        if (!matchesTextFilter(rawValue, filter)) return false;
      }
      return true;
    });
  }, [columns, columnValues, filters, rows]);

  useEffect(() => {
    onFilteredRowsChange?.(filteredRows);
  }, [filteredRows, onFilteredRowsChange]);

  useEffect(() => {
    setPage(1);
  }, [rows, filters, pageSize, storageKey]);

  const totalPages = paginationEnabled ? Math.max(1, Math.ceil(filteredRows.length / Math.max(1, pageSize))) : 1;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageStart = paginationEnabled ? (safePage - 1) * pageSize : 0;
  const displayedRows = paginationEnabled ? filteredRows.slice(pageStart, pageStart + pageSize) : filteredRows;

  function getColumnText(column: FilterableColumnDef<T>, row: T): string {
    if (column.value) return stringifyCell(column.value(row));
    return '';
  }

  function getHeaderText(column: FilterableColumnDef<T>): string {
    return column.headerText || (typeof column.header === 'string' ? column.header : column.key);
  }

  function canFilter(column: FilterableColumnDef<T>): boolean {
    return canFilterColumns && column.filterEnabled !== false && column.menuEnabled !== false;
  }

  function canResize(column: FilterableColumnDef<T>): boolean {
    return canResizeColumns && column.resizeEnabled !== false && column.menuEnabled !== false;
  }

  function patchFilter(columnKey: string, patch: Partial<ColumnFilterState>) {
    setFilters((prev) => {
      const nextFilter: ColumnFilterState = { ...defaultFilter(), ...(prev[columnKey] ?? {}), ...patch };
      const next = { ...prev };
      if (isFilterActive(nextFilter, columnValues[columnKey])) next[columnKey] = nextFilter;
      else delete next[columnKey];
      return next;
    });
  }

  function clearFilter(columnKey: string) {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setValueSearch((prev) => ({ ...prev, [columnKey]: '' }));
  }

  function clearAllFilters() {
    setFilters({});
    setValueSearch({});
  }

  function toggleValue(columnKey: string, value: string) {
    const allValues = columnValues[columnKey] ?? [];
    const current = filters[columnKey] ?? defaultFilter();
    const selected = current.selectedValues === null ? allValues : current.selectedValues;
    const nextSelected = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value].sort((a, b) => a.localeCompare(b));
    patchFilter(columnKey, { selectedValues: nextSelected.length === allValues.length ? null : nextSelected });
  }

  function autosizeColumn(columnKey: string, inputRows: T[] = filteredRows) {
    const column = columns.find((c) => c.key === columnKey);
    if (!column) return;
    const label = getHeaderText(column);
    const minWidth = 90;
    const maxWidth = columnMaxWidth(label);
    let width = estimateTextWidth(label) + 34;
    for (const row of inputRows.slice(0, 200)) {
      width = Math.max(width, estimateTextWidth(getColumnText(column, row)));
      if (width >= maxWidth) break;
    }
    setColumnWidths((prev) => ({ ...prev, [columnKey]: Math.min(Math.max(width, minWidth), maxWidth) }));
  }

  function autosizeAllColumns() {
    const next: Record<string, number> = {};
    for (const column of columns) {
      if (!canResize(column)) continue;
      const label = getHeaderText(column);
      const minWidth = 90;
      const maxWidth = columnMaxWidth(label);
      let width = estimateTextWidth(label) + 34;
      for (const row of filteredRows.slice(0, 200)) {
        width = Math.max(width, estimateTextWidth(getColumnText(column, row)));
        if (width >= maxWidth) break;
      }
      next[column.key] = Math.min(Math.max(width, minWidth), maxWidth);
    }
    setColumnWidths((prev) => ({ ...prev, ...next }));
  }

  function resetColumn(columnKey: string) {
    setColumnWidths((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }

  function resetAllColumns() {
    setColumnWidths({});
  }

  const activeFilterCount = (Object.entries(filters) as Array<[string, ColumnFilterState]>).filter(([columnKey, filter]) =>
    isFilterActive(filter, columnValues[columnKey]),
  ).length;
  const activeWidthCount = Object.keys(columnWidths).length;

  return (
    <div className={`space-y-2 ${wrapperClassName}`}>
      {(activeFilterCount > 0 || activeWidthCount > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground px-0.5">
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900 px-2 py-1">
              {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} rows after {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
            </span>
          )}
          {activeFilterCount > 0 && canFilterColumns && (
            <button type="button" className="underline hover:text-foreground" onClick={clearAllFilters}>Clear all filters</button>
          )}
          {activeWidthCount > 0 && canResizeColumns && (
            <button type="button" className="underline hover:text-foreground" onClick={resetAllColumns}>Reset all column widths</button>
          )}
        </div>
      )}

      {paginationEnabled && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <div>
            Showing {filteredRows.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pageSize, filteredRows.length).toLocaleString()} of {filteredRows.length.toLocaleString()} row{filteredRows.length === 1 ? '' : 's'}
            {filteredRows.length !== rows.length ? <span> after filters from {rows.length.toLocaleString()} cached row{rows.length === 1 ? '' : 's'}</span> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2">
              Rows
              <select
                className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                value={pageSize}
                onChange={(event) => setPageSize(Math.max(1, Number(event.target.value) || defaultPageSize))}
              >
                {(normalizedPageSizes.length ? normalizedPageSizes : [defaultPageSize]).map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </label>
            <button type="button" className="rounded border border-border px-2 py-1 hover:bg-background disabled:opacity-50" disabled={safePage <= 1} onClick={() => setPage(1)}>First</button>
            <button type="button" className="rounded border border-border px-2 py-1 hover:bg-background disabled:opacity-50" disabled={safePage <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Prev</button>
            <span>Page {safePage.toLocaleString()} of {totalPages.toLocaleString()}</span>
            <button type="button" className="rounded border border-border px-2 py-1 hover:bg-background disabled:opacity-50" disabled={safePage >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>Next</button>
            <button type="button" className="rounded border border-border px-2 py-1 hover:bg-background disabled:opacity-50" disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>Last</button>
          </div>
        </div>
      )}

      <div className={`overflow-auto ${maxHeightClassName}`}>
        <table className={`${tableClassName} border-separate border-spacing-0`}>
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-border">
              {columns.map((column) => {
                const filter = filters[column.key];
                const allValues = columnValues[column.key] ?? [];
                const active = isFilterActive(filter, allValues);
                const width = columnWidths[column.key];
                const menuAllowed = canFilter(column) || canResize(column);
                const widthStyle = width ? { width, minWidth: width, maxWidth: width } : undefined;

                return (
                  <th
                    key={column.key}
                    className={`text-left font-semibold text-muted-foreground bg-muted text-xs px-3 py-2 border-b border-border whitespace-nowrap relative align-top ${column.headerClassName ?? ''}`}
                    style={{ ...(column.headerStyle ?? {}), ...(widthStyle ?? {}) }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {column.onSort ? (
                        <button type="button" className="inline-flex items-center gap-1 truncate hover:text-foreground" onClick={column.onSort}>
                          {column.sortLabel ?? column.header}
                        </button>
                      ) : (
                        <span className="truncate" title={getHeaderText(column)}>{column.header}</span>
                      )}
                      {active && <span className="text-blue-600 dark:text-blue-300" title="Filter active">●</span>}
                      {menuAllowed && (
                        <button
                          type="button"
                          className="ml-auto rounded border border-border bg-background px-1.5 py-0.5 text-[11px] leading-none text-muted-foreground hover:text-foreground hover:bg-muted"
                          onClick={(event) => { event.stopPropagation(); setOpenColumnMenu(openColumnMenu === column.key ? null : column.key); }}
                          title={`Column options for ${getHeaderText(column)}`}
                        >
                          ⋯
                        </button>
                      )}
                    </div>

                    {openColumnMenu === column.key && (
                      <div className="absolute left-2 top-full mt-1 z-50 w-72 rounded-lg border border-border bg-background text-foreground shadow-xl p-3 font-normal whitespace-normal">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="font-bold text-sm truncate" title={getHeaderText(column)}>{getHeaderText(column)}</div>
                          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setOpenColumnMenu(null)}>Close</button>
                        </div>

                        {canFilter(column) && (
                          <div className="space-y-2 border-b border-border pb-3 mb-3">
                            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              <span>Filter</span>
                              {active && <button type="button" className="ml-auto normal-case underline" onClick={() => clearFilter(column.key)}>Clear</button>}
                            </div>
                            <select
                              className="w-full border border-border bg-background text-foreground rounded px-2 py-1.5 text-xs"
                              value={(filter ?? defaultFilter()).operator}
                              onChange={(event) => patchFilter(column.key, { operator: event.target.value as FilterOperator })}
                            >
                              {FILTER_OPERATORS.map((operator) => (
                                <option key={operator.value} value={operator.value}>{operator.label}</option>
                              ))}
                            </select>

                            {!['is_empty', 'is_not_empty'].includes((filter ?? defaultFilter()).operator) && (
                              <input
                                className="w-full border border-border bg-background text-foreground rounded px-2 py-1.5 text-xs"
                                placeholder="Filter..."
                                value={(filter ?? defaultFilter()).text}
                                onChange={(event) => patchFilter(column.key, { text: event.target.value })}
                              />
                            )}

                            <input
                              className="w-full border border-border bg-background text-foreground rounded px-2 py-1.5 text-xs"
                              placeholder="Search values..."
                              value={valueSearch[column.key] ?? ''}
                              onChange={(event) => setValueSearch((prev) => ({ ...prev, [column.key]: event.target.value }))}
                            />

                            <div className="flex gap-2 text-xs">
                              <button type="button" className="underline" onClick={() => patchFilter(column.key, { selectedValues: null })}>Select all</button>
                              <button type="button" className="underline" onClick={() => patchFilter(column.key, { selectedValues: [] })}>Clear all</button>
                            </div>

                            <div className="max-h-44 overflow-auto border border-border rounded bg-muted/20 p-1 space-y-1">
                              {allValues
                                .filter((value) => value.toLowerCase().includes((valueSearch[column.key] ?? '').toLowerCase()))
                                .slice(0, 150)
                                .map((value) => {
                                  const selectedValues = filter?.selectedValues ?? null;
                                  const checked = selectedValues === null || selectedValues.includes(value);
                                  return (
                                    <label key={`${column.key}-${value}`} className="flex items-start gap-2 px-1.5 py-1 rounded hover:bg-background cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={checked}
                                        onChange={() => toggleValue(column.key, value)}
                                      />
                                      <span className="break-all text-xs">{value || <em className="text-muted-foreground">(empty)</em>}</span>
                                    </label>
                                  );
                                })}
                              {allValues.length === 0 && <div className="text-xs text-muted-foreground px-1.5 py-1">No values found.</div>}
                              {allValues.length > 150 && <div className="text-[11px] text-muted-foreground px-1.5 py-1">Showing first 150 values. Use search to narrow.</div>}
                            </div>
                          </div>
                        )}

                        {canResize(column) && (
                          <div className="space-y-1 text-xs">
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={() => autosizeColumn(column.key)}>Autosize this column</button>
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={autosizeAllColumns}>Autosize all columns</button>
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={() => resetColumn(column.key)}>Reset this column</button>
                            <button type="button" className="block w-full text-left rounded px-2 py-1.5 hover:bg-muted" onClick={resetAllColumns}>Reset all columns</button>
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              displayedRows.map((row, offset) => {
                const rowIndex = paginationEnabled ? pageStart + offset : offset;
                return (
                <tr
                  key={rowKey ? rowKey(row, rowIndex) : rowIndex}
                  className={rowClassName ? rowClassName(row, rowIndex) : 'border-b border-border hover:bg-muted/50'}
                  onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                >
                  {columns.map((column) => {
                    const width = columnWidths[column.key];
                    const widthStyle = width ? { width, minWidth: width, maxWidth: width } : undefined;
                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-2 border-b border-border align-top ${column.cellClassName ?? ''}`}
                        style={{ ...(column.cellStyle ?? {}), ...(widthStyle ?? {}) }}
                      >
                        {column.render ? column.render(row, rowIndex) : getColumnText(column, row)}
                      </td>
                    );
                  })}
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
